import {skipToken} from '@reduxjs/toolkit/query';

import {isNilOrEmpty, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useGetServiceCaseOrderVariantsQuery,
  useGetServiceOrderIntegrationsQuery,
  useGetServiceOrderQuery,
  useGetServiceOrderVariantQuery,
} from '@dms/api/workshop';
import {usePermissions} from '@dms/shared';

import {useServiceCaseDetailBootstrap} from './useServiceCaseDetailBootstrap';

// Chronos changes the report action outside DMS, so no mutation invalidates it - poll instead.
const ORDER_INTEGRATIONS_POLLING_INTERVAL = 60_000;

interface UseOrderCardDataParams {
  serviceCaseId: string;
  serviceOrderId: string;
  isExpanded: boolean;
  serviceOrderVariantId?: string;
}

export const useOrderCardData = (params: UseOrderCardDataParams) => {
  const serviceOrderResult = useGetServiceOrderQuery(
    {
      serviceCaseId: params.serviceCaseId,
      serviceOrderId: params.serviceOrderId,
    },
    {
      skip:
        !params.isExpanded ||
        isNilOrEmpty(params.serviceCaseId) ||
        isNilOrEmpty(params.serviceOrderId),
    }
  );

  const {isBootstrapSettled} = useServiceCaseDetailBootstrap(params.serviceCaseId);
  const variantsResult = useGetServiceCaseOrderVariantsQuery(
    isBootstrapSettled && isNotNilOrEmpty(params.serviceCaseId)
      ? {serviceCaseId: params.serviceCaseId}
      : skipToken
  );

  // Jobs can mount while order details load; warm their variant query from the list payload.
  useGetServiceOrderVariantQuery(
    params.isExpanded &&
      isNotNilOrEmpty(params.serviceOrderVariantId) &&
      isNotNil(params.serviceOrderVariantId)
      ? {serviceOrderVariantId: params.serviceOrderVariantId}
      : skipToken
  );

  const [canReadChronos] = usePermissions({permissionKeys: ['chronosUser']});

  const integrationsResult = useGetServiceOrderIntegrationsQuery(
    {
      serviceCaseId: params.serviceCaseId,
      serviceOrderId: params.serviceOrderId,
    },
    {
      skip:
        !params.isExpanded ||
        isNilOrEmpty(params.serviceCaseId) ||
        isNilOrEmpty(params.serviceOrderId),
      pollingInterval: canReadChronos ? ORDER_INTEGRATIONS_POLLING_INTERVAL : undefined,
      skipPollingIfUnfocused: true,
      // Without this, re-expanding within keepUnusedDataFor re-serves stale cache without a fetch.
      refetchOnMountOrArgChange: true,
    }
  );

  return {
    serviceOrder: serviceOrderResult.currentData,
    variants: variantsResult.currentData,
    integrations: integrationsResult.currentData,
    isBootstrapSettled,
    isIntegrationsLoading: integrationsResult.isLoading,
    isIntegrationsError: integrationsResult.isError,
    isLoading: serviceOrderResult.isLoading || variantsResult.isLoading || !isBootstrapSettled,
    isError: serviceOrderResult.isError || variantsResult.isError,
  };
};
